import { Container, Icon } from 'gu-ui-kit'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import React, { useMemo } from 'react'
import { ROUTES } from 'constants/routes'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import { createPath } from 'history'
import qs from 'qs'
import prepareFilterParams from 'utils/prepareFilterParams'
import pick from 'lodash/pick'
import { formatByLocale } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import CellStyles from 'modules/Statistics/Account/cells.module.scss'
import clsx from 'clsx'

export const useTableConfig = ({
  filters = { include_vat: true },
  summary,
  sorting,
  setSorting,
  columnVisibility,
  setColumnVisibility,
  network,
  externalId,
  className = '',
}) => {
  const {
    i18n,
    user: { id: userId },
  } = useSelector(({ app }) => app)

  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const columns = useMemo(() => {
    let commonColumns

    switch (network) {
      case 'vk':
        return [
          {
            id: 'campaign.id',
            header: t('statistics::table.columns.header.campaign_id'),
            accessorKey: 'campaign.id',
            className: 'campaign-id-cell sticky',
            minWidth: 100,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  campaign: { id },
                },
              },
            }) => id,
          },
          {
            id: 'campaign.name',
            header: t('statistics::table.columns.header.campaign'),
            accessorKey: 'campaign.name',
            className: 'campaign-cell sticky',
            minWidth: 255,
            enableHiding: false,
            // eslint-disable-next-line react/prop-types
            cell: ({
              row: {
                original: {
                  campaign: { name },
                },
              },
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={name}
              >
                <div className={'overflow'}>
                  <span className={styles['login']}>{name}</span>
                </div>
              </Container>
            ),
          },
          {
            id: 'campaign.status',
            header: t('statistics::table.columns.header.status'),
            accessorKey: 'campaign.status',
            width: 130,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  campaign: { status },
                },
              },
            }) => {
              switch (status.toLowerCase()) {
                case 'active':
                  return (
                    <Icon
                      name={'status-on'}
                      size={'s'}
                      color={'secondary-10'}
                    />
                  )
                case 'archived':
                  return (
                    <Icon
                      name={'status-archive'}
                      size={'s'}
                      color={'basic-secondary-grey'}
                    />
                  )
                case 'paused':
                  return (
                    <Icon
                      name={'status-pause'}
                      size={'s'}
                      color={'basic-primary-grey'}
                    />
                  )
                default:
                  return '—'
              }
            },
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}: {table.getRowCount()}
              </b>
            ),
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { impressions },
              },
            }) => (impressions === null ? '—' : formatByLocale(impressions)),
            footer: () => (
              <b>{summary?.impressions === null ? '—' : formatByLocale(summary?.impressions)}</b>
            ),
          },
          {
            id: 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            cell: ({
              row: {
                original: { cost, cost_with_vat },
              },
            }) => {
              const val = filters.include_vat ? cost_with_vat : cost

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'] === null
                  ? '—'
                  : formatByLocale(
                      summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'],
                      i18n,
                      {
                        maximumFractionDigits: 2,
                      },
                    )}
              </b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { clicks },
              },
            }) => (clicks === null ? '—' : formatByLocale(clicks)),
            footer: () => <b>{summary?.clicks === null ? '—' : formatByLocale(summary?.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 80,
            width: 100,
            cell: ({
              row: {
                original: { ctr },
              },
            }) =>
              ctr === null
                ? '—'
                : formatByLocale(ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.ctr === null
                  ? '—'
                  : formatByLocale(summary?.ctr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
          {
            id: 'avg_click_price',
            header: t(
              `statistics::table.columns.header.${
                filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
              }`,
            ),
            accessorKey: filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price',
            minWidth: 160,
            cell: ({
              row: {
                original: { avg_click_price, avg_click_price_with_vat },
              },
            }) => {
              const val = filters.include_vat ? avg_click_price_with_vat : avg_click_price

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'] ===
                null
                  ? '—'
                  : formatByLocale(
                      summary?.[
                        filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
                      ],
                      i18n,
                      { maximumFractionDigits: 2 },
                    )}
              </b>
            ),
          },
        ]
      case 'vkads':
        return [
          {
            id: 'campaign.external_id',
            header: t('statistics::table.columns.header.campaign_id'),
            accessorKey: 'campaign.external_id',
            className: 'campaign-id-cell sticky',
            minWidth: 100,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  campaign: { external_id },
                },
              },
            }) => external_id,
          },
          {
            id: 'campaign.name',
            header: t('statistics::table.columns.header.campaign'),
            accessorKey: 'campaign.name',
            className: 'campaign-cell sticky',
            minWidth: 255,
            enableHiding: false,
            // eslint-disable-next-line react/prop-types
            cell: ({
              row: {
                original: {
                  campaign: { name },
                },
              },
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={name}
              >
                <div className={'overflow'}>
                  <span className={styles['login']}>{name}</span>
                </div>
              </Container>
            ),
          },
          {
            id: 'campaign.status',
            header: t('statistics::table.columns.header.status'),
            accessorKey: 'campaign.status',
            width: 100,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  campaign: { status },
                },
              },
            }) => {
              switch (status) {
                case 'active':
                  return (
                    <Icon
                      name={'status-on'}
                      size={'s'}
                      color={'secondary-10'}
                    />
                  )
                case 'deleted':
                  return (
                    <Icon
                      name={'status-archive'}
                      size={'s'}
                      color={'basic-secondary-grey'}
                    />
                  )
                case 'blocked':
                  return (
                    <Icon
                      name={'status-pause'}
                      size={'s'}
                      color={'basic-primary-grey'}
                    />
                  )
                default:
                  return '—'
              }
            },
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}: {table.getRowCount()}
              </b>
            ),
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { impressions },
              },
            }) => (impressions === null ? '—' : formatByLocale(impressions)),
            footer: () => (
              <b>{summary?.impressions === null ? '—' : formatByLocale(summary?.impressions)}</b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { clicks },
              },
            }) => (clicks === null ? '—' : formatByLocale(clicks)),
            footer: () => <b>{summary?.clicks === null ? '—' : formatByLocale(summary?.clicks)}</b>,
          },

          {
            id: 'goals',
            header: t('statistics::table.columns.header.goals'),
            accessorKey: 'goals',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { goals },
              },
            }) => (goals === null ? '—' : formatByLocale(goals)),
            footer: () => <b>{summary?.goals === null ? '—' : formatByLocale(summary?.goals)}</b>,
          },

          {
            id: 'spent',
            header: t(`table.columns.header.${filters.include_vat ? 'spent_with_vat' : 'spent'}`),
            accessorKey: filters.include_vat ? 'spent_with_vat' : 'spent',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { spent, spent_with_vat },
              },
            }) => {
              const val = filters.include_vat ? spent_with_vat : spent

              return val === null ? '—' : formatByLocale(val)
            },
            footer: () => {
              const name = filters.include_vat ? 'spent_with_vat' : 'spent'
              return <b>{summary?.[name] === null ? '—' : formatByLocale(summary?.[name])}</b>
            },
          },

          {
            id: 'cpm',
            header: t(`table.columns.header.${filters.include_vat ? 'cpm_with_vat' : 'cpm'}`),
            accessorKey: filters.include_vat ? 'cpm_with_vat' : 'cpm',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { cpm_with_vat, cpm },
              },
            }) => {
              const val = filters.include_vat ? cpm_with_vat : cpm

              return val === null ? '—' : formatByLocale(val)
            },
            footer: () => {
              const name = filters.include_vat ? 'cpm_with_vat' : 'cpm'
              return <b>{summary?.[name] === null ? '—' : formatByLocale(summary?.[name])}</b>
            },
          },

          {
            id: 'cpc',
            header: t(`table.columns.header.${filters.include_vat ? 'cpc_with_vat' : 'cpc'}`),
            accessorKey: filters.include_vat ? 'cpc_with_vat' : 'cpc',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { cpc_with_vat, cpc },
              },
            }) => {
              const val = filters.include_vat ? cpc_with_vat : cpc

              return val === null ? '—' : formatByLocale(val)
            },
            footer: () => {
              const name = filters.include_vat ? 'cpc_with_vat' : 'cpc'
              return <b>{summary?.[name] === null ? '—' : formatByLocale(summary?.[name])}</b>
            },
          },

          {
            id: 'cpa',
            header: t(`table.columns.header.${filters.include_vat ? 'cpa_with_vat' : 'cpa'}`),
            accessorKey: filters.include_vat ? 'cpa_with_vat' : 'cpa',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { cpa_with_vat, cpa },
              },
            }) => {
              const val = filters.include_vat ? cpa_with_vat : cpa
              return val === null ? '—' : formatByLocale(val)
            },
            footer: () => {
              const name = filters.include_vat ? 'cpa_with_vat' : 'cpa'
              return <b>{summary?.[name] === null ? '—' : formatByLocale(summary?.[name])}</b>
            },
          },

          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 80,
            width: 80,
            cell: ({
              row: {
                original: { ctr },
              },
            }) =>
              ctr === null
                ? '—'
                : formatByLocale(ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.ctr === null
                  ? '—'
                  : formatByLocale(summary?.ctr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
          {
            id: 'cr',
            header: t('statistics::table.columns.header.cr'),
            accessorKey: 'cr',
            minWidth: 80,
            width: 80,
            cell: ({
              row: {
                original: { cr },
              },
            }) =>
              cr === null
                ? '—'
                : formatByLocale(cr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.cr === null
                  ? '—'
                  : formatByLocale(summary?.cr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
        ]
      case 'googleads':
      case 'yandexdirect':
        commonColumns = [
          {
            id: 'campaign.id',
            header: t('statistics::table.columns.header.campaign_id'),
            accessorKey: 'campaign.id',
            className: 'campaign-id-cell sticky',
            minWidth: 100,
            enableHiding: false,
            footerColSpan: 2,
            // eslint-disable-next-line react/prop-types
            cell: ({
              row: {
                original: {
                  campaign: { id },
                },
              },
            }) => (
              <Link
                className={styles['login']}
                to={createPath({
                  pathname: pathGen(ROUTES.STATISTICS_CAMPAIGN, {
                    network,
                    accountExternalId: externalId,
                    campaignId: id,
                  }),
                  search: qs.stringify(
                    prepareFilterParams(
                      pick(filters, ['date_group_period', 'date_from', 'date_to']),
                    ),
                    { arrayFormat: 'brackets' },
                  ),
                })}
              >
                {id}
              </Link>
            ),
            footer: ({ table }) => (
              <b className={styles['summary-label']}>
                {t('statistics::table.footer.summary')}:{' '}
                {t('statistics::table.footer.campaigns', { count: table.getRowCount() })}
              </b>
            ),
          },
          {
            id: 'campaign.name',
            header: t('statistics::table.columns.header.campaign'),
            accessorKey: 'campaign.name',
            className: 'campaign-name-cell sticky',
            minWidth: 190,
            enableHiding: false,
            footerColSpan: 0,
            cell: ({
              // eslint-disable-next-line react/prop-types
              row: {
                // eslint-disable-next-line react/prop-types
                original: {
                  campaign: { id, name },
                },
              },
              // eslint-disable-next-line react/prop-types
            }) => (
              <Container
                display="flex"
                className={styles['login-cell']}
                data-content={name}
              >
                <div className={'overflow'}>{name ?? id}</div>
              </Container>
            ),
          },
          {
            id: 'campaign.state',
            header: t('statistics::table.columns.header.state'),
            accessorKey: 'campaign.state',
            width: 130,
            enableHiding: false,
            cell: ({
              row: {
                original: {
                  campaign: { state },
                },
              },
            }) => {
              switch (state.toLowerCase()) {
                case 'on':
                  return (
                    <Icon
                      name={'status-on'}
                      size={'s'}
                      color={'secondary-10'}
                    />
                  )
                case 'off':
                  return (
                    <Icon
                      name={'status-blocked'}
                      size="s"
                      color={'text-error'}
                    />
                  )
                case 'archived':
                  return (
                    <Icon
                      name={'status-archive'}
                      size={'s'}
                      color={'basic-secondary-grey'}
                    />
                  )
                case 'suspended':
                  return (
                    <Icon
                      name={'status-finished'}
                      size={'s'}
                      color={'secondary-13'}
                    />
                  )
                case 'ended':
                  return (
                    <Icon
                      name={'status-blocked'}
                      size="s"
                      color={'text-error'}
                    />
                  )
                default:
                  return '—'
              }
            },
            footer: () => <></>,
          },
          {
            id: 'impressions',
            header: t('statistics::table.columns.header.impressions'),
            accessorKey: 'impressions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { impressions },
              },
            }) => (impressions === null ? '—' : formatByLocale(impressions)),
            footer: () => (
              <b>{summary?.impressions === null ? '—' : formatByLocale(summary?.impressions)}</b>
            ),
          },
          {
            id: 'cost',
            header: t(
              `statistics::table.columns.header.${filters.include_vat ? 'cost_with_vat' : 'cost'}`,
            ),
            accessorKey: filters.include_vat ? 'cost_with_vat' : 'cost',
            minWidth: 150,
            cell: ({
              row: {
                original: { cost, cost_with_vat },
              },
            }) => {
              const val = filters.include_vat ? cost_with_vat : cost

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'] === null
                  ? '—'
                  : formatByLocale(
                      summary?.[filters.include_vat ? 'cost_with_vat' : 'cost'],
                      i18n,
                      {
                        maximumFractionDigits: 2,
                      },
                    )}
              </b>
            ),
          },
          {
            id: 'revenue',
            header: t('statistics::table.columns.header.revenue_by_target'),
            accessorKey: 'revenue',
            minWidth: 100,
            width: 140,
            cell: ({
              row: {
                original: { revenue },
              },
            }) => (revenue === null ? '—' : formatByLocale(revenue)),
            footer: () => (
              <b>{summary?.revenue === null ? '—' : formatByLocale(summary?.revenue)}</b>
            ),
          },
          {
            id: 'clicks',
            header: t('statistics::table.columns.header.clicks'),
            accessorKey: 'clicks',
            minWidth: 80,
            width: 100,
            cell: ({
              row: {
                original: { clicks },
              },
            }) => (clicks === null ? '—' : formatByLocale(clicks)),
            footer: () => <b>{summary?.clicks === null ? '—' : formatByLocale(summary?.clicks)}</b>,
          },
          {
            id: 'ctr',
            header: t('statistics::table.columns.header.ctr'),
            tooltip: t('statistics::table.columns.header.ctr_tooltip'),
            accessorKey: 'ctr',
            minWidth: 70,
            width: 70,
            cell: ({
              row: {
                original: { ctr },
              },
            }) =>
              ctr === null
                ? '—'
                : formatByLocale(ctr, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.ctr === null
                  ? '—'
                  : formatByLocale(summary?.ctr, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
          {
            id: 'avg_click_price',
            header: t(
              `statistics::table.columns.header.${
                filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
              }`,
            ),
            accessorKey: filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price',
            minWidth: 165,
            cell: ({
              row: {
                original: { avg_click_price, avg_click_price_with_vat },
              },
            }) => {
              const val = filters.include_vat ? avg_click_price_with_vat : avg_click_price

              return val === null
                ? '—'
                : formatByLocale(val, i18n, {
                    maximumFractionDigits: 2,
                  })
            },
            footer: () => (
              <b>
                {summary?.[filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'] ===
                null
                  ? '—'
                  : formatByLocale(
                      summary?.[
                        filters.include_vat ? 'avg_click_price_with_vat' : 'avg_click_price'
                      ],
                      i18n,
                      { maximumFractionDigits: 2 },
                    )}
              </b>
            ),
          },
          {
            id: 'conversions',
            header: t('statistics::table.columns.header.conversions'),
            accessorKey: 'conversions',
            minWidth: 120,
            width: 120,
            cell: ({
              row: {
                original: { conversions },
              },
            }) => (conversions === null ? '—' : formatByLocale(conversions)),
            footer: () => (
              <b>{summary?.conversions === null ? '—' : formatByLocale(summary?.conversions)}</b>
            ),
          },
          {
            id: 'conversion_rate',
            header: t('statistics::table.columns.header.conversion_rate'),
            accessorKey: 'conversion_rate',
            minWidth: 100,
            width: 120,
            cell: ({
              row: {
                original: { conversion_rate },
              },
            }) =>
              conversion_rate === null
                ? '—'
                : formatByLocale(conversion_rate, i18n, {
                    maximumFractionDigits: 2,
                  }),
            footer: () => (
              <b>
                {summary?.conversion_rate === null
                  ? '—'
                  : formatByLocale(summary?.conversion_rate, i18n, {
                      maximumFractionDigits: 2,
                    })}
              </b>
            ),
          },
        ]

        return summary?.google_analytics_metrics && summary?.google_analytics_metrics
          ? commonColumns.concat({
              header: <span className={CellStyles['header-cell__super']}>Google Analytics</span>,
              footer: () => <span></span>,
              canSort: false,
              id: 'google_analytics',
              className: clsx('grouped-cell', CellStyles['google_analytics_cell']),
              columns: [
                'bounce_rate',
                'bounces',
                'drr',
                'new_users',
                'pageviews',
                'roi',
                'session_duration',
                'sessions',
                'transaction_revenue',
                'transactions',
                'users',
              ].map((columnName) => ({
                id: 'google_analytics_metrics.' + columnName,
                header: t('statistics::table.columns.ga_header.' + columnName),
                minWidth: 100,
                width: 150,
                accessorKey: 'google_analytics_metrics.' + columnName,
                className: clsx('grouped-cell', CellStyles['google_analytics_cell']),
                cell: ({ cell }) =>
                  !cell.getValue()
                    ? '—'
                    : formatByLocale(cell.getValue(), i18n, {
                        maximumFractionDigits: 2,
                      }),
                footer: () => (
                  <b>
                    {summary?.google_analytics_metrics?.[columnName] === null
                      ? '—'
                      : formatByLocale(summary?.google_analytics_metrics?.[columnName], i18n, {
                          maximumFractionDigits: 2,
                        })}
                  </b>
                ),
              })),
            })
          : commonColumns
    }
  }, [filters?.include_vat, i18n, userId, externalId, network, summary])

  return {
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    showHeader: true,
    showFooter: true,
    enableRowSelection: false,
    className,
  }
}
